/**
 * View as member — this tab's state (Backend/admin_view_as.py).
 *
 * An admin opens a member's account from User 360 → "View as member": a new
 * tab with /#view_as=<one-time code>, which utils/viewAs.ts trades for a token.
 * The token and who it stands for live HERE, in sessionStorage — one tab
 * only — and apiService reads the signed-in user from here first, so the
 * admin's own sign-in (localStorage, shared by every tab) is never read,
 * overwritten or signed out by the view-as tab. Once the view ends the entry
 * stays, marked ended, so the tab shows "ended" rather than falling back to
 * the admin's own account.
 *
 * No imports: apiService and the analytics modules read this.
 */

export interface ViewAs {
  token: string;
  user_id: string;
  email: string;
  name: string;
  mode: 'read' | 'write';
  expires_at: string;
  admin_email: string;
  session_id?: string;
  ended?: boolean;
}

const KEY = 'utech_view_as';
export const VIEW_AS_HEADER = 'X-Utech-View-As';
/** Fired when this tab's view starts, ends or expires. */
export const VIEW_AS_CHANGED_EVENT = 'utech:view-as-changed';

export function viewAs(): ViewAs | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && v.token ? v as ViewAs : null;
  } catch {
    return null;
  }
}

/** True for a view-as tab, live or ended: such a tab never reads or writes
 *  the admin's own stored sign-in. */
export function isViewAsTab(): boolean {
  return viewAs() !== null;
}

/** A view-as tab, or one about to become one (/#view_as=… not yet claimed):
 *  what the analytics check before they start, which is before the claim. */
export function isViewAsOrPending(): boolean {
  if (isViewAsTab()) return true;
  try { return /(?:^#|&)view_as=/.test(window.location.hash); } catch { return false; }
}

/** The live view, or null (none, ended, or past its end). */
export function liveViewAs(): ViewAs | null {
  const v = viewAs();
  if (!v || v.ended) return null;
  if (Date.parse(v.expires_at) <= Date.now()) return null;
  return v;
}

export function setViewAs(v: ViewAs): void {
  try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch { /* storage off */ }
  try { window.dispatchEvent(new Event(VIEW_AS_CHANGED_EVENT)); } catch { /* no window */ }
}

export function markViewAsEnded(): void {
  const v = viewAs();
  if (!v || v.ended) return;
  setViewAs({ ...v, ended: true });
}
